import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Pencil } from 'lucide-react';
import styles from './ScenarioGenerator.module.css';
import familyIconStyles from '../common/ImageModelFamilySelector.module.css';
import { useToast } from '../../../hooks/useToast';
import { ScenarioActorSwapModal } from './ScenarioActorSwapModal';
import { ScenarioCharactersSection } from './ScenarioCharactersSection';
import { MotionScriptConvertSection } from './voice/MotionScriptConvertSection';
import { derivePositionalReplacements } from './actorReplacement';
import { useGenerationState, formatVideoResolutionLabel, videoResolutionSortValue } from '../state/useGenerationState';
import type { CastingActor, SeedancePresetActorItem } from '../state/types';
import { listAmazonProducts } from '../../../features/amazon/api';
import type { UpdateScenarioPayload, ScenarioActorReplacementResult } from '../../../features/scenarios/types';
import {
  VideoModelFamilySelector,
  PROVIDER_META,
  videoModelButtonLabel,
  videoModelGroupLabel,
} from '../common/VideoModelFamilySelector';
import { useUpdateScenario } from '../../../features/scenarios/hooks';
import { useMaterialWorkflowStages } from '../../../features/materials/hooks';
import { ORPHAN_WORKFLOW_STAGE_HINT, resolveWorkflowStageLabel } from '../../../features/materials/workflowStageLabel';
import { useProduct } from '../../../features/products/hooks';
import { deriveLoadedVideoGenerationMode } from '../../../features/scenarios/productionMode';
import { scenarioModelDisplayName } from './scenarioModelCatalog';
import type { GenerationModel } from '../../../features/generation-models/api';
import { brandConfig } from '../../../config/brands';
import type { Scenario, ScenarioResponse } from './types';
import type { VideoSettings } from './VideoGenerationSettings';
import type { UseScenarioInfoModalReturn } from './hooks/useScenarioInfoModal';
import { useSceneHandlers } from './hooks/SceneHandlersContext';
import { castRoleForIndex, scenarioCastRoles } from './dialogueLines';

const FRAME_MODE_OPTIONS = [
  { value: 'first_only' as const, label: '最初のフレームのみ' },
  { value: 'first_and_last' as const, label: '最初と最後のフレームをセットで生成' },
  { value: 'first_with_next' as const, label: '次のシーンの最初を最後のフレームに設定' },
];

interface ScenarioInfoModalProps {
  modal: UseScenarioInfoModalReturn;
  scenarios: Scenario[];
  activeScenarioId: string | null;
  videoGenerationModels: GenerationModel[];
  lastResult: ScenarioResponse | null;
  setLastResult: React.Dispatch<React.SetStateAction<ScenarioResponse | null>>;
  setVideoSettings: React.Dispatch<React.SetStateAction<VideoSettings>>;
  updateScenarioMutation: ReturnType<typeof useUpdateScenario>;
  /** 現在のユーザー (app_users.user_uid)。所有者のみ編集できるため、一致しなければ「編集」を出さない
   *  (BE の update_scenario も所有者検証で拒否する。計画 §3.4.3) */
  currentUserId?: string;
}

export function ScenarioInfoModal({
  modal,
  scenarios,
  activeScenarioId,
  videoGenerationModels,
  lastResult,
  setLastResult,
  setVideoSettings,
  updateScenarioMutation,
  currentUserId,
}: ScenarioInfoModalProps) {
  const toast = useToast();

  // AI俳優差し替えの反映先。動画/フレーム生成のペイロードはシナリオレコードでは
  // なく settings コンテキスト (selectedCasting 等) から組まれるため、保存成功時に
  // ここへも同期しないと「保存したのに次の生成は旧俳優」という乖離が起きる。
  const {
    setSelectedCasting, setAdditionalCastings,
    setSelectedSeedancePresetActor, setAdditionalSeedancePresetActors,
  } = useGenerationState();

  // 動画生成ボタンはシーン単位 override (sceneVideoSettings) を最優先で使うため、
  // デフォルト動画モデル/解像度の保存時は videoSettings だけでなく既存シーンの
  // override も更新しないと「保存したのに生成は旧モデル」という乖離が起きる。
  const { applyScenarioVideoSettingsToScenes } = useSceneHandlers();

  // モーダルは既定でリードオンリー表示。「編集」でエディットモードに切り替え、
  // キャンセル / 保存 / モーダルクローズで戻る。俳優・動画モデルの選択は
  // それぞれ二段目のモーダルで行う。閉じたら次回オープン時のためにリセットする。
  const [isEditMode, setIsEditMode] = useState(false);
  const [actorSwapOpen, setActorSwapOpen] = useState(false);
  // チップごとの「入れ替え」ボタンから開く単一選択モード (§3.4)。配列位置の
  // index を持つ (null = 閉じている)。「変更」(actorSwapOpen) とは排他。
  const [actorReplaceIndex, setActorReplaceIndex] = useState<number | null>(null);
  const [modelSwapOpen, setModelSwapOpen] = useState(false);
  useEffect(() => {
    if (!modal.isOpen) {
      setIsEditMode(false);
      setActorSwapOpen(false);
      setActorReplaceIndex(null);
      setModelSwapOpen(false);
    }
  }, [modal.isOpen]);

  // 制作進捗のステージ一覧 (素材と共有の組織別定義)。フックなので早期 return より前で呼ぶ。
  // 組織に有効ステージが無ければ項目ごと出さない (ゲート)。計画 §3.4.3 / §3.4.5
  const workflowStagesQuery = useMaterialWorkflowStages();
  const workflowStages = workflowStagesQuery.data ?? [];
  const showWorkflowStage = workflowStages.length > 0;

  if (!modal.isOpen) return null;

  const currentScenario = scenarios.find((s) => s.id === (activeScenarioId ?? scenarios[0]?.id));
  if (!currentScenario) return null;

  // モード判定は generation_metadata の production_mode/per_scene_default_mode
  // (無ければレガシー video_generation_mode/frame_mode) を正本にする合成ヘルパー経由
  // (P2 step 7-6・§9 分類A)。frame_mode 単独だと、ボード未生成で frame_mode が
  // storyboard へ未昇格の storyboard シナリオ (gm=storyboard / frame_mode=first_only) を
  // frame と誤判定し、動画モデルのロックが外れ・フレームモード UI が露出してしまう
  // (docs/requirements/storyboard-mode-loading-fix-plan.md)。
  const derivedVideoMode = deriveLoadedVideoGenerationMode(currentScenario);

  // ストーリーボードモードでは、生成済みのストーリーボード画像が選択中の動画モデル
  // (Veo / Seedance) を前提として作られている。後から切り替えると絵コンテと整合
  // しなくなるため、モデル変更モーダル内で注意書きを出す (編集自体は俳優差し替え等
  // のために許可する)。
  const isStoryboardMode = derivedVideoMode === 'storyboard';

  // 「フレームモード」(first_only / first_and_last / first_with_next) の3択は、
  // フレームモード系のシナリオでのみ意味を持つ。reference / storyboard モードでは
  // この設定は使われないため、項目自体を隠す。reference モードでは open() 時に
  // editingFrameMode が 'first_only' へフォールバックするため、表示したままだと
  // 保存時に frame_mode を 'first_only' で上書きしてしまう不具合もある。
  const isFrameMode = derivedVideoMode === 'frame';

  const {
    editingScenarioTitle,
    setEditingScenarioTitle,
    editingScenarioSummary,
    setEditingScenarioSummary,
    editingFrameMode,
    setEditingFrameMode,
    editingTags,
    setEditingTags,
    editingVideoModel,
    setEditingVideoModel,
    editingResolution,
    setEditingResolution,
    editingCastings,
    setEditingCastings,
    editingPresetActors,
    setEditingPresetActors,
    editingWorkflowStageId,
    setEditingWorkflowStageId,
    originalScenarioInfo,
    close,
    reset,
  } = modal;

  // AI俳優差し替え: 使う俳優リスト (通常AI俳優 / Seedance preset) は動画モデルの
  // ファミリーで決まる (既存 CastingSection と同じ prefix 判定)。
  const isSeedanceFamily = (m: string) => (m || '').startsWith('seedance');
  const editingIsSeedance = isSeedanceFamily(editingVideoModel);
  const actorFamilyChanged = editingIsSeedance !== isSeedanceFamily(originalScenarioInfo.videoModel);
  const castingIdsChanged = JSON.stringify(editingCastings.map((c) => c.id))
    !== JSON.stringify(originalScenarioInfo.castingIds);
  const presetIdsChanged = JSON.stringify(editingPresetActors.map((a) => a.id))
    !== JSON.stringify(originalScenarioInfo.presetActorIds);
  // 保存時に俳優フィールドを payload へ含める条件。ファミリーを跨いだ場合は
  // 選択が同じでも「使わない側のクリア」を保存する必要がある。
  const actorFieldsDirty = castingIdsChanged || presetIdsChanged || actorFamilyChanged;
  // 表示・保存に使う「有効な側」の俳優リスト。
  const activeActors: (CastingActor | SeedancePresetActorItem)[] = editingIsSeedance
    ? editingPresetActors
    : editingCastings;
  // 選択上限 = 登場人物数。未設定レガシーは 1。既に上限超の選択が保存されている
  // 場合は現状の人数まで許容する (開いた瞬間に選択が壊れて見えるのを防ぐ)。
  const actorCap = Math.max(
    1,
    currentScenario.character_count ?? 1,
    editingCastings.length,
    editingPresetActors.length,
  );

  // 保存前プレビュー (§3.4): 保存前の配列と編集中の配列から位置置換が導出できる
  // かどうかを FE 側でも計算する (バックエンド正本 derive_positional_replacements
  // と同じ規則を actorReplacement.ts に移植)。ファミリーが変わった場合は
  // §3.5 により対象外 (サーバーも同じ理由で applied: false になる)。
  const originalActiveActorIds = editingIsSeedance
    ? originalScenarioInfo.presetActorIds
    : originalScenarioInfo.castingIds;
  const editingActiveActorIds = activeActors.map((a) => a.id);
  const derivedActorReplacements = (actorFieldsDirty && !actorFamilyChanged)
    ? derivePositionalReplacements(originalActiveActorIds, editingActiveActorIds)
    : null;
  // 「旧名」表示用。保存前 (= DB 上の現在値) の俳優詳細は currentScenario 側に
  // 残っているので、そこから id → display_name を引く (editingCastings 等は
  // 既にユーザー編集後の値で上書きされているため使えない)。
  const originalActorListForPreview = editingIsSeedance
    ? ((currentScenario.seedance_preset_actors && currentScenario.seedance_preset_actors.length > 0)
      ? currentScenario.seedance_preset_actors
      : (currentScenario.seedance_preset_actor ? [currentScenario.seedance_preset_actor] : []))
    : ((currentScenario.casting_models && currentScenario.casting_models.length > 0)
      ? currentScenario.casting_models
      : (currentScenario.casting ? [currentScenario.casting] : []));
  const originalActorDisplayNameById = new Map(
    originalActorListForPreview.map((a) => [a.id, a.display_name]),
  );
  const editingActorDisplayNameById = new Map(activeActors.map((a) => [a.id, a.display_name]));

  // モデルごとの解像度の値域 (DB の generation_models.supported_resolutions 由来)。
  // VideoGenerationSettings と同じ導出規則: 未収載モデルは Veo の既存値域に
  // フォールバックし、降順 (1080 → 720 → 480) で表示する。Gemini Omni は 720 固定
  // なので、ここを表引きにしないと「1080p を選べるのに実際は 720p」の齟齬が出る。
  const resolutionsForModel = (code: string): string[] => {
    const list = videoGenerationModels.find((m) => m.model_code === code)?.supported_resolutions;
    const base = Array.isArray(list) && list.length > 0 ? list : ['720', '1080'];
    return [...base].sort((a, b) => videoResolutionSortValue(b) - videoResolutionSortValue(a));
  };
  const availableResolutions = resolutionsForModel(editingVideoModel);
  const bestResolutionFor = (code: string): string => {
    const avail = resolutionsForModel(code);
    // 既定は 720p 優先 (VideoGenerationSettings の正規化と同じ方針。2026-08-18)。
    return avail.includes('720') ? '720' : avail[0];
  };

  // 表示は「ファミリー名 - モード名」。ファミリー名 (プロバイダ表記) だけだと
  // 同一ファミリー内のモード (標準 / スピード / ミニ) が判別できないため、
  // generation_models.display_label をモード名として後置する。
  const modelDisplayLabel = (code: string): string => {
    const model = videoGenerationModels.find((m) => m.model_code === code);
    if (!model) return code;
    const family = videoModelGroupLabel(model);
    const mode = videoModelButtonLabel(model).trim();
    return mode && mode !== family ? `${family} - ${mode}` : family;
  };

  // 編集モードへの切り替え。保存済みの解像度が現行モデルの値域外 (レガシー / モデル
  // 変更後のデータ) の場合はここで有効値へ補正し、保存で DB 側も直るようにする。
  const enterEditMode = () => {
    if (!availableResolutions.includes(editingResolution)) {
      setEditingResolution(bestResolutionFor(editingVideoModel));
    }
    setIsEditMode(true);
  };

  const cancelEditMode = () => {
    reset();
    setIsEditMode(false);
  };

  const tagsChanged = JSON.stringify(editingTags) !== JSON.stringify(originalScenarioInfo.tags);
  const frameModeChanged = isFrameMode && editingFrameMode !== originalScenarioInfo.frameMode;
  // 制作進捗。無効化済みステージを参照しているときは「無効なステージ」と表示し、
  // 触らない限り既存値を維持する (§3.4.5)。
  const resolvedWorkflowStage = resolveWorkflowStageLabel(editingWorkflowStageId, workflowStages);
  const workflowStageChanged = editingWorkflowStageId !== originalScenarioInfo.workflowStageId;
  // 所有者のみ編集可。user_id が無いシナリオは安全側に倒して編集不可 (通常経路では必ず入る。§3.4.1)
  const canEdit = !!currentUserId && !!currentScenario.user_id && currentScenario.user_id === currentUserId;
  const hasChanges = editingScenarioTitle !== originalScenarioInfo.title
    || editingScenarioSummary !== originalScenarioInfo.summary
    || frameModeChanged
    || editingVideoModel !== originalScenarioInfo.videoModel
    || editingResolution !== originalScenarioInfo.resolution
    || tagsChanged
    || castingIdsChanged
    || presetIdsChanged
    || workflowStageChanged;

  const handleClose = () => {
    if (hasChanges) {
      if (!window.confirm('変更が保存されていません。破棄してよろしいですか？')) return;
    }
    close();
  };

  // 生成時メタ情報（read-only）。シナリオ生成に使われた LLM モデルや
  // 生成条件を控えめに掲示する。値が無いものは行ごと出さない。
  const meta = (currentScenario.generation_metadata ?? {}) as Record<string, unknown>;
  const metaRows: { key: string; value: string }[] = [];
  if (typeof meta.model === 'string' && meta.model) {
    // 生の model id ("gemini-3.6-flash" 等) ではなく、ドロップダウン等と同じ
    // 表示名 ("Gemini 3.6 Flash") をカタログから導出して見せる。
    metaRows.push({ key: '生成モデル', value: scenarioModelDisplayName(meta.model) });
  }
  if (currentScenario.aspect_ratio) {
    metaRows.push({ key: 'アスペクト比', value: currentScenario.aspect_ratio });
  }
  if (typeof meta.tone === 'string' && meta.tone) {
    metaRows.push({ key: 'トーン', value: meta.tone });
  }
  if (typeof meta.duration === 'string' && meta.duration) {
    metaRows.push({ key: '想定尺', value: meta.duration });
  }
  if (typeof meta.elapsed_seconds === 'number') {
    metaRows.push({ key: 'シナリオ生成所要時間', value: `${Math.round(meta.elapsed_seconds)}秒` });
  }
  if (currentScenario.created_at) {
    const created = new Date(currentScenario.created_at);
    if (!Number.isNaN(created.getTime())) {
      metaRows.push({
        key: '作成日時',
        value: created.toLocaleString('ja-JP', { dateStyle: 'medium', timeStyle: 'short' }),
      });
    }
  }
  // シナリオ生成時にユーザーが入力した追加プロンプト (生テキストがそのまま
  // generation_metadata に入っている)。改行を含む長文になり得るため、右寄せの
  // key/value 行ではなく積み上げブロックで見せる。
  const additionalPromptText = typeof meta.additional_prompt === 'string'
    ? meta.additional_prompt.trim()
    : '';

  // 祖先に transform/filter を持つ要素があると position:fixed の包含ブロックが
  // ズレるため、document.body へポータルしてビューポート基準で中央配置する。
  // 編集フォームを含むモーダルのため、オーバーレイの誤クリックでは閉じない
  // (閉じるのは ✕ ボタン / Esc / 保存のみ)。
  return createPortal(
    <div className="modal-overlay">
      <div className={`modal-card ${styles.scenarioInfoCard}`}>
        <div className="modal-header">
          <h3 className="modal-title">シナリオ情報</h3>
          <button
            type="button"
            className="modal-close-button"
            onClick={handleClose}
            aria-label="閉じる"
          >
            ✕
          </button>
        </div>
        <div className={`modal-body ${styles.scenarioInfoColumns}`}>
          {/* 左列: 基本情報 */}
          <div className={styles.scenarioInfoColumn}>
          {/* 基本情報 */}
          <div className={styles.scenarioInfoSection}>
            <div className={styles.scenarioInfoField}>
              <label className={styles.scenarioInfoLabel}>タイトル</label>
              {isEditMode ? (
                <input
                  type="text"
                  className={styles.scenarioInfoInput}
                  value={editingScenarioTitle}
                  onChange={(e) => setEditingScenarioTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      handleClose();
                    }
                  }}
                  autoFocus
                  placeholder="シナリオタイトル"
                />
              ) : (
                <div style={{ fontSize: '0.9rem', color: 'rgba(245, 241, 230, 0.9)' }}>
                  {editingScenarioTitle || '(無題)'}
                </div>
              )}
            </div>
            <div className={styles.scenarioInfoField}>
              <label className={styles.scenarioInfoLabel}>概要</label>
              {isEditMode ? (
                <textarea
                  className={styles.scenarioInfoTextarea}
                  value={editingScenarioSummary}
                  onChange={(e) => setEditingScenarioSummary(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      handleClose();
                    }
                  }}
                  rows={3}
                  placeholder="シナリオの概要を入力"
                />
              ) : (
                <div style={{ fontSize: '0.82rem', color: 'rgba(245, 241, 230, 0.75)', whiteSpace: 'pre-wrap' }}>
                  {editingScenarioSummary || <span style={{ color: 'rgba(245, 241, 230, 0.4)' }}>概要なし</span>}
                </div>
              )}
            </div>
            <div className={styles.scenarioInfoField}>
              <label className={styles.scenarioInfoLabel}>{brandConfig.labels.tag}</label>
              {isEditMode ? (
                <div className={styles.editableTagsList}>
                  {editingTags.map((tag, tagIdx) => (
                    <div key={tagIdx} className={styles.editableTagItem}>
                      <input
                        type="text"
                        className={styles.editableTagInput}
                        value={tag}
                        onChange={(e) => {
                          const newTags = [...editingTags];
                          newTags[tagIdx] = e.target.value;
                          setEditingTags(newTags);
                        }}
                      />
                      <button
                        type="button"
                        className={styles.editableTagDelete}
                        onClick={() => setEditingTags(editingTags.filter((_, i) => i !== tagIdx))}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className={styles.editableTagAdd}
                    onClick={() => setEditingTags([...editingTags, ''])}
                  >
                    + タグを追加
                  </button>
                </div>
              ) : (
                <div style={{ fontSize: '0.82rem', color: 'rgba(245, 241, 230, 0.75)' }}>
                  {editingTags.filter((t) => t.trim() !== '').length > 0
                    ? editingTags.filter((t) => t.trim() !== '').join('、')
                    : <span style={{ color: 'rgba(245, 241, 230, 0.4)' }}>なし</span>}
                </div>
              )}
            </div>
          </div>
          {showWorkflowStage && (
            <div className={styles.scenarioInfoField}>
              <label className={styles.scenarioInfoLabel}>制作進捗</label>
              {isEditMode ? (
                <select
                  value={editingWorkflowStageId ?? ''}
                  onChange={(e) => setEditingWorkflowStageId(e.target.value ? e.target.value : null)}
                  disabled={updateScenarioMutation.isPending}
                  className={`${styles.scenarioInfoInput} ${styles.scenarioInfoSelect}`}
                  title={resolvedWorkflowStage.kind === 'orphan' ? ORPHAN_WORKFLOW_STAGE_HINT : undefined}
                >
                  <option value="">未設定</option>
                  {workflowStages.map((stage) => (
                    <option key={stage.id} value={stage.id}>{stage.label}</option>
                  ))}
                  {resolvedWorkflowStage.kind === 'orphan' && editingWorkflowStageId && (
                    // 無効化済みステージを参照している場合、現在の値を選択肢に残して既存値を維持できるようにする
                    <option value={editingWorkflowStageId}>（無効）現在の値</option>
                  )}
                </select>
              ) : (
                <div
                  style={{ fontSize: '0.85rem', color: 'rgba(245, 241, 230, 0.85)' }}
                  title={resolvedWorkflowStage.kind === 'orphan' ? ORPHAN_WORKFLOW_STAGE_HINT : undefined}
                >
                  {resolvedWorkflowStage.label}
                </div>
              )}
            </div>
          )}

          {/* 商品情報 */}
          <ScenarioProductInfo scenario={currentScenario} />
          </div>

          {/* 中央列: 映像設定 */}
          <div className={styles.scenarioInfoColumn}>
          <div className={styles.scenarioInfoSection}>
            <div className={styles.scenarioInfoSectionTitle}>映像設定</div>
            {/* アスペクト比はシナリオ全体で単一の値であるべきで、生成後に変更しても
                既存シーンは旧比率のまま → シーン間で混在するという「あり得ない」状態を
                生むだけ。設定画面で確定するプロパティのため、ここでは編集させない。 */}
            {/* 動画生成モデルを先頭に置く: 対応解像度・AI俳優の値域はモデルで決まる
                (親設定 → 従属設定の順)。 */}
            <div className={styles.scenarioInfoField}>
              <label className={styles.scenarioInfoLabel}>デフォルト動画生成モデル</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ fontSize: '0.85rem', color: 'rgba(245, 241, 230, 0.85)', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  {(() => {
                    // ファミリー名の左にプロバイダアイコン (VideoModelFamilySelector
                    // のヘッダーと同じマスク方式) を添える。
                    const provider = videoGenerationModels.find((m) => m.model_code === editingVideoModel)?.provider;
                    const pm = provider ? PROVIDER_META[provider] : undefined;
                    if (pm?.iconMask) {
                      return (
                        <span
                          className={familyIconStyles.modelFamilyIconMask}
                          role="img"
                          aria-label={pm.label}
                          style={{
                            WebkitMaskImage: `url("${pm.iconMask}")`,
                            maskImage: `url("${pm.iconMask}")`,
                          }}
                        />
                      );
                    }
                    return pm?.icon ? <span role="img" aria-label={pm.label}>{pm.icon}</span> : null;
                  })()}
                  <span>{modelDisplayLabel(editingVideoModel)}</span>
                </div>
                {isEditMode && (
                  <button
                    type="button"
                    className="btn-modal-secondary-sm"
                    onClick={() => setModelSwapOpen(true)}
                  >
                    変更
                  </button>
                )}
              </div>
            </div>
            <div className={styles.scenarioInfoField}>
              <label className={styles.scenarioInfoLabel}>デフォルト出力解像度</label>
              {isEditMode ? (
                <div className={styles.infoAspectRatioButtons}>
                  {availableResolutions.map((res) => (
                    <button
                      key={res}
                      type="button"
                      className={`${styles.infoAspectRatioButton} ${editingResolution === res ? styles.infoAspectRatioButtonActive : ''}`}
                      onClick={() => setEditingResolution(res)}
                    >
                      {formatVideoResolutionLabel(res)}
                    </button>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: '0.85rem', color: 'rgba(245, 241, 230, 0.85)' }}>
                  {formatVideoResolutionLabel(editingResolution)}
                </div>
              )}
            </div>
            <div className={styles.scenarioInfoField}>
              <label className={styles.scenarioInfoLabel}>AI俳優</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                {activeActors.length === 0 ? (
                  <span style={{ fontSize: '0.82rem', color: 'rgba(245, 241, 230, 0.5)' }}>未設定</span>
                ) : (
                  activeActors.map((actor, idx) => {
                    const imageUrl = 'face_image_public_url' in actor
                      ? actor.face_image_public_url
                      : (actor as SeedancePresetActorItem).thumbnail_public_url;
                    return (
                      <span key={actor.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt=""
                            style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover' }}
                          />
                        ) : null}
                        <span style={{ fontSize: '0.82rem', color: 'rgba(245, 241, 230, 0.85)' }}>
                          {actorCap > 1 ? `${String.fromCharCode(65 + idx)}: ` : ''}
                          {/* 役柄名 (シナリオ上の呼び名) を主にし、俳優の表示名はキャスト選択の手がかりとして添える */}
                          {(() => {
                            const role = castRoleForIndex(scenarioCastRoles(currentScenario), idx);
                            return role ? `${role}（${actor.display_name}）` : actor.display_name;
                          })()}
                        </span>
                        {isEditMode && (
                          <button
                            type="button"
                            className="btn-modal-secondary-sm"
                            onClick={() => setActorReplaceIndex(idx)}
                          >
                            入れ替え
                          </button>
                        )}
                      </span>
                    );
                  })
                )}
                {isEditMode && (
                  <button
                    type="button"
                    className="btn-modal-secondary-sm"
                    onClick={() => setActorSwapOpen(true)}
                  >
                    変更
                  </button>
                )}
              </div>
              {isEditMode && actorFamilyChanged && (
                <span style={{ fontSize: '0.7rem', color: 'rgba(245, 200, 120, 0.75)', marginTop: '4px', display: 'block' }}>
                  動画生成モデルの系統が変わったため、俳優は選び直しが必要です（保存時に旧モデル用の俳優選択はクリアされます）。
                </span>
              )}
              {isEditMode && actorFieldsDirty && !actorFamilyChanged && (
                <>
                  <span style={{ fontSize: '0.7rem', color: 'rgba(245, 241, 230, 0.45)', marginTop: '4px', display: 'block' }}>
                    俳優の変更は保存後の生成（絵コンテ・フレーム・動画）から反映されます。生成済みの内容は変わりません。
                  </span>
                  {derivedActorReplacements && derivedActorReplacements.length > 0 ? (
                    <div style={{ marginTop: '4px' }}>
                      {derivedActorReplacements.map((r) => (
                        <span
                          key={r.position}
                          style={{ fontSize: '0.7rem', color: 'rgba(160, 210, 170, 0.85)', display: 'block' }}
                        >
                          {String.fromCharCode(65 + r.position)}: {originalActorDisplayNameById.get(r.fromId) ?? '(不明)'}
                          {' → '}
                          {editingActorDisplayNameById.get(r.toId) ?? '(不明)'}
                          （シーン本文の @参照と話者を更新します）
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span style={{ fontSize: '0.7rem', color: 'rgba(245, 241, 230, 0.45)', marginTop: '4px', display: 'block' }}>
                      本文の @参照は更新されません。
                    </span>
                  )}
                </>
              )}
            </div>
            {/* 登場人物 (キャスト以外の一意な人物。docs/features/scenario-telop-speaker-plan.md §3.7) */}
            <ScenarioCharactersSection scenario={currentScenario} setLastResult={setLastResult} />
            {/* 旧契約のモーション台本だけ: 声の台本 (v2) への変換 (docs/features/brand-cut-motion-graphics-plan.md §10.7.5) */}
            <MotionScriptConvertSection scenario={currentScenario} setLastResult={setLastResult} />
            {isFrameMode && (
              <div className={styles.scenarioInfoField}>
                <label className={styles.scenarioInfoLabel}>フレームモード</label>
                {isEditMode ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {FRAME_MODE_OPTIONS.map(({ value, label }) => (
                      <label key={value} className="form-checkbox-label">
                        <input
                          type="radio"
                          name="editingFrameMode"
                          checked={editingFrameMode === value}
                          onChange={() => setEditingFrameMode(value)}
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '0.85rem', color: 'rgba(245, 241, 230, 0.85)' }}>
                    {FRAME_MODE_OPTIONS.find((o) => o.value === editingFrameMode)?.label ?? editingFrameMode}
                  </div>
                )}
              </div>
            )}
          </div>
          </div>

          {/* 右列: 生成情報（read-only） */}
          <div className={styles.scenarioInfoColumn}>
          {(metaRows.length > 0 || additionalPromptText) && (
            <div className={styles.scenarioInfoSection}>
              <div className={styles.scenarioInfoSectionTitle}>生成情報</div>
              {metaRows.length > 0 && (
                <dl className={styles.scenarioInfoMeta}>
                  {metaRows.map((row) => (
                    <div key={row.key} className={styles.scenarioInfoMetaRow}>
                      <dt className={styles.scenarioInfoMetaKey}>{row.key}</dt>
                      <dd className={styles.scenarioInfoMetaValue}>{row.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {additionalPromptText && (
                <div className={styles.scenarioInfoMetaBlock}>
                  <span className={styles.scenarioInfoLabel}>シナリオ生成の追加プロンプト</span>
                  {/* グローバルの `.modal-body p` (font-size:14px / margin-bottom:14px) が
                      CSS モジュールの単一クラスより詳細度で勝つため、<p> は使わない。
                      概要の閲覧表示と同じ <div> で揃える。 */}
                  <div className={styles.scenarioInfoMetaBlockValue}>{additionalPromptText}</div>
                </div>
              )}
            </div>
          )}
          </div>
        </div>

        {/* フッター: 閲覧モードでは「編集」のみ、編集モードではキャンセル / 保存 */}
        {!isEditMode ? (
          <div className={styles.scenarioInfoFooter} style={{ justifyContent: 'flex-start' }}>
            {/* 所有者のみ編集できる (他ユーザーの共有シナリオは閲覧のみ。BE も所有者検証で拒否する) */}
            {canEdit && (
              <button
                type="button"
                className="btn-modal-secondary"
                onClick={enterEditMode}
              >
                <Pencil size={14} aria-hidden="true" />
                編集
              </button>
            )}
          </div>
        ) : (
        <div className={styles.scenarioInfoFooter} style={{ justifyContent: 'space-between' }}>
            <button
              type="button"
              className="btn-cancel"
              disabled={updateScenarioMutation.isPending}
              onClick={cancelEditMode}
            >
              キャンセル
            </button>
            <button
              type="button"
              className="btn-modal btn-modal-accent"
              disabled={updateScenarioMutation.isPending || !hasChanges}
              onClick={async () => {
                try {
                  const payload: UpdateScenarioPayload = {};
                  if (editingScenarioTitle !== originalScenarioInfo.title) {
                    payload.title = editingScenarioTitle;
                  }
                  if (editingScenarioSummary !== originalScenarioInfo.summary) {
                    payload.summary = editingScenarioSummary;
                  }
                  if (frameModeChanged) {
                    payload.frame_mode = editingFrameMode;
                  }
                  if (tagsChanged) {
                    payload.tags = editingTags.filter((t) => t.trim() !== '');
                  }
                  if (editingVideoModel !== originalScenarioInfo.videoModel) {
                    payload.video_model = editingVideoModel;
                  }
                  if (editingResolution !== originalScenarioInfo.resolution) {
                    payload.output_resolution = editingResolution;
                  }
                  // 制作進捗は変更があったときだけ送る (差分方式)。未設定を選んだら null。
                  // 変更が無い保存では送らず既存値を維持する (無効化済みステージでも 400 にしない)
                  if (workflowStageChanged) {
                    payload.workflow_stage_id = editingWorkflowStageId;
                  }
                  // AI俳優: 有効な側 (モデルファミリー準拠) の選択を保存し、使わない
                  // 側は [] で明示クリアする (BE は配列そのまま + 単数ミラー更新)。
                  const savedCastings = editingIsSeedance ? [] : editingCastings;
                  const savedPresetActors = editingIsSeedance ? editingPresetActors : [];
                  if (actorFieldsDirty) {
                    payload.casting_model_ids = savedCastings.map((c) => c.id);
                    payload.seedance_preset_actor_ids = savedPresetActors.map((a) => a.id);
                    // ファミリーを跨いだ変更では旧配列 → [] のクリアであり位置置換の
                    // 対象外 (§3.5)。同一ファミリー内の差し替えのときだけ、シーン本文の
                    // @参照・話者帰属の追随をサーバーに依頼する。
                    if (!actorFamilyChanged) {
                      payload.apply_actor_replacements = true;
                    }
                  }

                  let actorReplacementResult: ScenarioActorReplacementResult | undefined;

                  if (Object.keys(payload).length > 0) {
                    const updateResult = await updateScenarioMutation.mutateAsync({
                      scenarioId: currentScenario.id,
                      payload,
                    });
                    actorReplacementResult = updateResult.actor_replacement;

                    if (lastResult) {
                      const replacedScenesById = new Map(
                        (actorReplacementResult?.applied ? actorReplacementResult.replaced_scenes : undefined)
                          ?.map((rs) => [rs.id, rs]) ?? [],
                      );
                      setLastResult((prev) => {
                        if (!prev) return prev;
                        const updatedScenarios = prev.scenarios.map((s) =>
                          s.id === currentScenario.id
                            ? {
                                ...s,
                                ...(payload.title !== undefined && { title: payload.title }),
                                ...(payload.summary !== undefined && { summary: payload.summary }),
                                ...(payload.frame_mode !== undefined && { frame_mode: payload.frame_mode as Scenario['frame_mode'] }),
                                ...(payload.tags !== undefined && { tags: payload.tags }),
                                ...(payload.video_model !== undefined && { video_model: payload.video_model }),
                                ...(payload.output_resolution !== undefined && { output_resolution: payload.output_resolution }),
                                ...(payload.workflow_stage_id !== undefined && { workflow_stage_id: payload.workflow_stage_id }),
                                ...(actorFieldsDirty && {
                                  casting_model_ids: savedCastings.map((c) => c.id),
                                  casting_models: savedCastings,
                                  casting: savedCastings[0],
                                  seedance_preset_actor_ids: savedPresetActors.map((a) => a.id),
                                  seedance_preset_actors: savedPresetActors,
                                  seedance_preset_actor: savedPresetActors[0],
                                  seedance_preset_actor_id: savedPresetActors[0]?.id,
                                }),
                                ...(replacedScenesById.size > 0 && {
                                  scenes: s.scenes.map((scene) => {
                                    const rs = replacedScenesById.get(scene.id);
                                    if (!rs) return scene;
                                    return {
                                      ...scene,
                                      ...(rs.visual !== undefined && { visual: rs.visual }),
                                      ...(rs.setting !== undefined && { setting: rs.setting }),
                                      ...(rs.narration !== undefined && { narration: rs.narration }),
                                      ...(rs.telop !== undefined && { telop: rs.telop }),
                                      ...(rs.dialogue !== undefined && { dialogue: rs.dialogue }),
                                      ...(rs.script !== undefined && { script: rs.script }),
                                      ...(rs.dialogue_lines !== undefined && { dialogue_lines: rs.dialogue_lines }),
                                    };
                                  }),
                                }),
                              }
                            : s,
                        );
                        return { ...prev, scenarios: updatedScenarios };
                      });
                    }
                  }

                  if (editingVideoModel !== originalScenarioInfo.videoModel) {
                    setVideoSettings((prev) => ({ ...prev, videoModel: editingVideoModel as VideoSettings['videoModel'] }));
                  }
                  if (editingResolution !== originalScenarioInfo.resolution) {
                    setVideoSettings((prev) => ({ ...prev, resolution: editingResolution as VideoSettings['resolution'] }));
                  }
                  if (
                    editingVideoModel !== originalScenarioInfo.videoModel
                    || editingResolution !== originalScenarioInfo.resolution
                  ) {
                    applyScenarioVideoSettingsToScenes(currentScenario.id, {
                      ...(editingVideoModel !== originalScenarioInfo.videoModel && { videoModel: editingVideoModel }),
                      ...(editingResolution !== originalScenarioInfo.resolution && { resolution: editingResolution }),
                    });
                  }
                  if (actorFieldsDirty) {
                    // 生成ペイロードは settings コンテキストから組まれるため、保存した
                    // フルキャストを主 (=[0]) + 追加 (=[1..]) に振り分けて同期する。
                    setSelectedCasting(savedCastings[0] ?? null);
                    setAdditionalCastings(savedCastings.slice(1));
                    setSelectedSeedancePresetActor(savedPresetActors[0] ?? null);
                    setAdditionalSeedancePresetActors(savedPresetActors.slice(1));
                  }

                  if (actorReplacementResult?.applied) {
                    const replacedCount = actorReplacementResult.replaced_scenes?.length ?? 0;
                    toast.success(
                      `AI俳優を入れ替え、シーン${replacedCount}件の@参照と話者を更新しました。絵コンテ・動画は再生成が必要です`,
                    );
                  } else {
                    toast.success('シナリオ情報を更新しました');
                  }
                  if (actorReplacementResult?.warnings?.length) {
                    actorReplacementResult.warnings.forEach((w) => toast.show(w));
                  }
                  close();
                } catch (err) {
                  const isActorReplacementConflict = axios.isAxiosError(err)
                    && err.response?.status === 409
                    && (err.response?.data as { code?: string } | undefined)?.code === 'actor_replacement_conflict';
                  if (isActorReplacementConflict) {
                    // §3.2: 失敗時は配列もシーンも一切変わっていない。この画面の
                    // lastResult は更新前提の内容を DB へ再取得するための汎用手段を
                    // 持たないため (lastResult はローカル state)、編集を破棄してユーザー
                    // に再読み込みを促すに留める (トースト文言どおり)。
                    toast.error('他の編集と競合しました。画面を再読み込みして再度お試しください');
                    close();
                    return;
                  }
                  toast.error('シナリオ情報の更新に失敗しました');
                }
              }}
            >
              保存
            </button>
        </div>
        )}
      </div>
      {modelSwapOpen && (
        <VideoModelSwapModal
          models={videoGenerationModels}
          activeModelCode={editingVideoModel}
          isStoryboardMode={isStoryboardMode}
          onApply={(code) => {
            setEditingVideoModel(code);
            // 新モデルの値域に無い解像度は有効値へ補正する (Omni は 720 固定等)。
            if (!resolutionsForModel(code).includes(editingResolution)) {
              setEditingResolution(bestResolutionFor(code));
            }
            setModelSwapOpen(false);
          }}
          onClose={() => setModelSwapOpen(false)}
        />
      )}
      {(actorSwapOpen || actorReplaceIndex !== null) && (
        <ScenarioActorSwapModal
          isSeedance={editingIsSeedance}
          cap={actorCap}
          initialCastings={editingCastings}
          initialPresetActors={editingPresetActors}
          replaceIndex={actorReplaceIndex ?? undefined}
          onApply={(castings, presetActors) => {
            // ピッカーが編集するのは有効な側だけ。もう一方は編集セッション中の
            // 値を保持する (保存時に有効側以外は [] でクリアされる)。
            if (editingIsSeedance) {
              setEditingPresetActors(presetActors);
            } else {
              setEditingCastings(castings);
            }
            setActorSwapOpen(false);
            setActorReplaceIndex(null);
          }}
          onClose={() => {
            setActorSwapOpen(false);
            setActorReplaceIndex(null);
          }}
        />
      )}
    </div>,
    document.body,
  );
}

/**
 * シナリオ情報モーダルから開く動画生成モデルの変更ピッカー (二段目モーダル)。
 * AI俳優ピッカーと同じ流儀で、選択はローカル state に留めて「適用」で親の編集
 * state へ書き戻す (シナリオへの保存は親モーダルの「保存」で行う)。
 */
function VideoModelSwapModal({
  models,
  activeModelCode,
  isStoryboardMode,
  onApply,
  onClose,
}: {
  models: GenerationModel[];
  activeModelCode: string;
  isStoryboardMode: boolean;
  onApply: (code: string) => void;
  onClose: () => void;
}) {
  const [selectedCode, setSelectedCode] = useState(activeModelCode);
  return createPortal(
    <div className="modal-overlay">
      <div className="modal-card" style={{ maxWidth: 640, width: 'min(640px, calc(100vw - 32px))' }}>
        <div className="modal-header">
          <h3 className="modal-title">動画生成モデルを変更</h3>
          <button
            type="button"
            className="modal-close-button"
            onClick={onClose}
            aria-label="閉じる"
          >
            ✕
          </button>
        </div>
        <div className="modal-body">
          <VideoModelFamilySelector
            models={models}
            activeModelCode={selectedCode}
            onSelect={(code) => setSelectedCode(code)}
          />
          {/* モデル説明は VideoModelFamilySelector のボタン hover ツールチップで表示する */}
          {isStoryboardMode && (
            <span style={{ fontSize: '0.7rem', color: 'rgba(245, 200, 120, 0.75)', marginTop: '6px', display: 'block' }}>
              生成済みの絵コンテ・動画は変更前のモデルを前提に作られています。変更は保存後の生成から反映されます。
              モデルの系統 (Seedance ⇄ その他) を跨ぐ場合は AI俳優の選び直しが必要です。
            </span>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px' }}>
          <button type="button" className="btn-cancel" onClick={onClose}>
            キャンセル
          </button>
          <button
            type="button"
            className="btn-modal"
            onClick={() => onApply(selectedCode)}
          >
            適用
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * シナリオに紐づく商品情報を表示するセクション。
 * 商品名のみを表示し、商品ページへのテキストリンクにする。
 * - 自社 manual 商品 (product_id / custom_input.product_id): 商品名 → /products/manual/:id
 * - Amazon (asin): ASIN/商品名 → 商品ページ or Amazon
 * - 手入力 (custom_input.product_name のみ): 入力値をそのまま表示 (リンク先ページ無し)。
 * 商品ページへの遷移で編集中の未保存内容を失わないよう、リンクは別タブで開く。
 */
function ScenarioProductInfo({ scenario }: { scenario: Scenario }) {
  const customInput = scenario.custom_input as Record<string, unknown> | undefined;
  const customProductId = typeof customInput?.product_id === 'string' ? customInput.product_id : null;
  const manualProductId = scenario.product_id || customProductId;
  const asin = scenario.asin;
  const customProductName = typeof customInput?.product_name === 'string' ? customInput.product_name : null;

  // manual 商品のときのみ詳細を取得 (enabled: !!productId はフック内で判定)。
  const { data: product } = useProduct(manualProductId ?? '');

  // Amazon 商品は ASIN から商品名を引くため一覧を取得 (ScenarioBookModal と同一
  // queryKey でキャッシュ共有)。asin が無いシナリオでは fetch しない。
  const { data: amazonData } = useQuery({
    queryKey: ['products', 'amazon', 'all'],
    queryFn: async () => {
      const data = await listAmazonProducts({ limit: 100 });
      return data as { products?: Array<{ asin: string; title?: string }> };
    },
    enabled: !!asin,
    staleTime: 5 * 60 * 1000,
  });

  const hasProduct = !!manualProductId || !!asin || !!customProductName;

  // 表示内容の組み立て
  let nameText = '';
  let detailHref: string | undefined;

  if (manualProductId) {
    nameText = product?.title || customProductName || 'マニュアル登録商品';
    detailHref = `/products/manual/${manualProductId}`;
  } else if (asin) {
    const amazonProduct = amazonData?.products?.find((p) => p.asin === asin);
    nameText = amazonProduct?.title || asin;
    detailHref = brandConfig.features.amazonIntegration
      ? `/products/amazon/${asin}`
      : `https://www.amazon.co.jp/dp/${asin}`;
  } else if (customProductName) {
    nameText = customProductName;
  }

  return (
    <div className={styles.scenarioInfoSection}>
      <div className={styles.scenarioInfoSectionTitle}>商品情報</div>
      {hasProduct ? (
        detailHref ? (
          <a
            href={detailHref}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.productBoxLink}
          >
            {nameText}
          </a>
        ) : (
          <div className={styles.scenarioInfoMetaValue}>{nameText}</div>
        )
      ) : (
        <div className={styles.scenarioInfoMetaValue} style={{ fontSize: '0.82rem', color: 'rgba(245, 241, 230, 0.5)' }}>
          このシナリオには商品が紐づいていません
        </div>
      )}
    </div>
  );
}
